import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { useRef, useState } from "react";
import { DropTargetMonitor, useDrag, useDrop, XYCoord } from "react-dnd";
import { Party } from "../models/party";

export type CurrentPartyProps = {
  party: Party;
  index: number;
  mode: string;
  register: any;
  moveParty: (dragIndex: number, hoverIndex: number) => void;
};

export interface DragItem {
  index: number;
  id: string;
  type: string;
}

const PartyItem = (props: CurrentPartyProps) => {
  const party = props.party;
  const formMode = props.mode;
  const index = props.index;
  const register = props.register;
  const moveParty = props.moveParty;
  const [checkedItem, setCheckedItem] = useState(true);

  const ref = useRef<HTMLDivElement>(null);
  const [, drop] = useDrop({
    accept: "current-party",
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover: (item: DragItem, monitor: DropTargetMonitor) => {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveParty(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: "current-party",
    item: () => {
      return { party, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`rounded-md border bg-white cursor-move shadow ${
        opacity === 0 ? "opacity-40" : ""
      }`}
    >
      <div className="flex gap-2 rounded-md bg-[#E6EBF5] py-2 pl-2 px-4 items-center">
        <div className="flex self-center -mr-1 opacity-50 cursor-move text-primary-blue">
          <EllipsisVerticalIcon className="h-4" />
          <EllipsisVerticalIcon className="h-4 -ml-3" />
        </div>
        <div className="flex items-center justify-center w-6 h-6 text-sm font-semibold bg-white border border-opacity-50 rounded-full border-primary-blue text-primary-blue">
          {index + 1}
        </div>
        <div className="flex items-center">
          <input
            checked={formMode === 'create' ? checkedItem : null}
            id={`parties.${index}.selected`}
            {...register(`parties.${index}.selected`)}
            type="checkbox"
            className="w-5 h-5 cursor-pointer"
            onClick={() => setCheckedItem((prev) => !prev)}
          />
        </div>
        <label htmlFor={`parties.${index}.selected`} className="font-normal">
          {party.name}
        </label>
      </div>
    </div>
  );
};

export default PartyItem;
